import React, { memo } from "react"
import clsx from "clsx"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { usePlanning } from "./PlanningContext"
import useDropTarget from "../hooks/useDropTarget"
import { dayTone } from "../lib/days"
import { formatHours } from "../lib/hours"

const ACCEPTS = ["project"]

// Dropping a project on a team's day plans every member of the team.
const GroupDay = memo(({ groupId, date, tone, planned, size }) => {
  const { handlers, canWrite } = usePlanning()
  const { over, dropProps } = useDropTarget({
    accepts: ACCEPTS,
    blocked: false,
    onDrop: drag => handlers.dropOnGroup({ groupId, date, drag }),
  })
  return (
    <div
      {...(canWrite ? dropProps : {})}
      title={I18n.t("project_planning.group.drop_hint")}
      className={clsx(
        "pp-drop relative flex items-center justify-center border-r border-b border-slate-200 transition-colors",
        tone === "bg-white" ? "bg-slate-50/80" : tone,
        over && "pp-over bg-brand-50 ring-2 ring-inset ring-brand-400",
      )}
    >
      {over ? (
        <span className="text-[10.5px] font-semibold text-brand-700 flex items-center gap-1">
          <FontAwesomeIcon icon="users" className="text-[10px]" />
          {I18n.t("project_planning.group.plan_all", { count: size })}
        </span>
      ) : (
        <span className={clsx("text-[10.5px] tabular-nums font-medium", planned === 0 ? "text-slate-300" : planned >= size ? "text-emerald-600" : "text-slate-500")}>
          {planned}/{size}
        </span>
      )}
    </div>
  )
})

const GroupRow = ({ group, size, days, template, collapsed, onToggle, plannedByDay, total }) => (
  <div className="grid" style={{ gridTemplateColumns: template }}>
    <button
      type="button"
      onClick={onToggle}
      className="sticky left-0 z-[5] flex items-center gap-2 px-3 h-8 bg-slate-50 border-r border-b border-slate-200 text-left hover:bg-slate-100 transition-colors"
    >
      <FontAwesomeIcon icon="chevron-right" className={clsx("text-[9px] text-slate-400 transition-transform duration-200", !collapsed && "rotate-90")} />
      <span className="text-[11.5px] font-semibold uppercase tracking-wide text-slate-600 truncate">
        {group.name ? I18n.t("project_planning.group.team_of", { name: group.name }) : I18n.t("project_planning.group.none")}
      </span>
      <span className="ml-auto text-[10.5px] font-semibold text-slate-400 tabular-nums">{size}</span>
    </button>
    {days.map(day => (
      <GroupDay key={day.date} groupId={group.id} date={day.date} tone={dayTone(day)} planned={plannedByDay[day.date] || 0} size={size} />
    ))}
    <div className="flex items-center justify-end px-3 bg-slate-50 border-b border-slate-200 text-[11px] font-semibold text-slate-500 tabular-nums">
      {formatHours(total)}
    </div>
  </div>
)

export default GroupRow
